import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, combineLatest, forkJoin, Subject } from "rxjs";
import { filter, map, switchMap, take, takeUntil } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStep, OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { SCORE_SESSION_ROUTE } from "./h01-home-introduction.service";
import { isOnRoute, openOnboardingCentredModal, whenUiQuiet } from "./onboarding-lesson-modal";
import { RS01_LESSON_ID, RS02_LESSON_ID } from "./rs-repeat-scoring.service";
import { ScoreContextualEntryService } from "./score-contextual-entry.service";

export const FO01_LESSON_ID = "FO01";

/** Recorded with FO01's completed lesson state - its CompletedAt *is* this milestone. */
export const SCORE_FLEXIBLE_ORDER_INTRODUCED = "score_flexible_order_introduced";

/**
 * Copy rows FO01.01-FO01.02, workbook v2.38 Copy sheet (verbatim). No button labels: FO01 has no
 * follow-up action, so the modal shows none (FLIP-500).
 */
export const FO01_COPY = {
  heading: "Score in the order that works for you",
  body:
    "You can begin with Stars or Shadows, depending on what feels easiest to identify right now. " +
    "Every Score needs at least one Star, but you can add your items in whichever order works for you.",
};

/**
 * Onboarding lesson FO01 "Score in the order that works for you" (FLIP-370; spec v2.36 §18.3,
 * workbook v2.38). Informational centred modal over the scoring session page.
 *
 * - Eligible while the `onboarding-walkthrough` flag is on and FO01 is unresolved, when the user
 *   begins the first genuinely new scoring session after one submitted Score. Genuinely new is
 *   the page's no-draft or Start fresh branch (onGenuinelyNewSession) - a recovered draft never
 *   counts, so that session passes without FO01. Any failed check holds back.
 * - Shows once the previous-Stars reuse decision has resolved (starsReady - the Shadows one only
 *   opens on switching tab, after FO01) and the UI is quiet, so it never stacks on the recovery or
 *   reuse modals, and only while the user is still on the session page.
 * - Never in an entry where RS01/RS02 appeared: skipped without a write, so it waits for the next
 *   genuinely new session.
 * - No action buttons (FLIP-500): there is no follow-up, the user is already on the session page.
 *   Every exit completes FO01: the close X / Escape / Back. Backdrop taps do nothing.
 */
@Injectable({ providedIn: "root" })
export class FO01FlexibleOrderService {
  private readonly leave$ = new Subject<void>();
  private readonly genuinelyNew$ = new BehaviorSubject<boolean>(false);
  private readonly starsReady$ = new BehaviorSubject<boolean>(false);
  private open = false;
  /** Completed in this app session - its write may still be in flight. */
  private completed = false;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private blockingUiSignal: BlockingUiSignalService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private entry: ScoreContextualEntryService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this account's state.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.completed = false;
      this.leave();
    });
  }

  /** Whether the FO01 modal is showing right now. */
  get isOpen(): boolean {
    return this.open;
  }

  /** Call when the scoring session page opens. */
  enter(): void {
    this.leave();
    this.genuinelyNew$.next(false);
    this.starsReady$.next(false);

    this.entry
      .enabled$()
      .pipe(
        switchMap(() =>
          forkJoin([this.onboardingStepService.getStep(FO01_LESSON_ID), this.onboardingStepService.getSubmittedScoreCount()])
        ),
        filter(([step, scoreCount]) => this.isEligible(step, scoreCount)),
        switchMap(([step]) =>
          combineLatest([this.genuinelyNew$, this.starsReady$]).pipe(
            filter(([isNew, ready]) => isNew && ready),
            take(1),
            switchMap(() => whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$)),
            filter(() => this.canShowNow()),
            map(() => step ?? null)
          )
        ),
        takeUntil(this.leave$)
      )
      .subscribe((step) => this.present(step));
  }

  /** Call when the session is a genuinely new Score - no draft, or the user chose Start fresh. */
  onGenuinelyNewSession(): void {
    this.genuinelyNew$.next(true);
  }

  /** Call once the previous-Stars reuse decision has resolved. */
  starsReady(): void {
    this.starsReady$.next(true);
  }

  /** Call when leaving the session page. An open modal closes on navigation (and completes). */
  leave(): void {
    this.leave$.next();
  }

  private isEligible(step: OnboardingStep | null | undefined, scoreCount: number | null): boolean {
    // Only definite answers qualify - "couldn't check" holds back rather than guessing.
    return (
      !this.completed &&
      step !== undefined &&
      !step?.completedAt &&
      !step?.dismissed &&
      scoreCount !== null &&
      scoreCount >= 1
    );
  }

  private canShowNow(): boolean {
    return (
      !this.completed &&
      !this.open &&
      isOnRoute(this.router, SCORE_SESSION_ROUTE) &&
      // Never stacks over, or follows, the repeat-scoring lesson in the same entry.
      !this.entry.presentedThisEntry(RS01_LESSON_ID, RS02_LESSON_ID)
    );
  }

  private present(step: OnboardingStep | null): void {
    this.open = true;
    this.entry.markPresented(FO01_LESSON_ID);
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: FO01_LESSON_ID,
      lastViewedAt: now,
      ...(step?.startedAt ? {} : { startedAt: now }),
    });
    if (!step?.startedAt) {
      this.track("onboarding_lesson_state_changed", "started");
    }

    const exit$ = openOnboardingCentredModal(this.dialog, FO01_COPY);
    this.track("onboarding_lesson_presented");
    exit$.subscribe(() => this.complete());
  }

  /** Informational: every exit completes FO01. */
  private complete(): void {
    this.open = false;
    this.completed = true;
    this.onboardingStepService.upsertStep({ stepKey: FO01_LESSON_ID, completedAt: new Date() });
    this.track("onboarding_lesson_action", "action: dismiss");
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${SCORE_FLEXIBLE_ORDER_INTRODUCED}`);
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(eventName: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${FO01_LESSON_ID}; ${detail}` : `lesson: ${FO01_LESSON_ID}`,
    });
  }
}
